<template>
  <div class="pt-4">
    <div class="inner py-0">
      <h1>Wholesaler Mapping</h1>
    </div>

    <div class="inner flex items-center justify-between gap-4 py-2">
      <div class="join justify-center">
        <input
          type="radio"
          name="wholesalerOption"
          aria-label="Wholesalers"
          class="btn join-item btn-sm"
          @click="mode = 'wholesalers'"
          :checked="mode === 'wholesalers'" />

        <input
          type="radio"
          name="wholesalerOption"
          aria-label="Wholesaler Groups"
          class="btn join-item btn-sm"
          @click="mode = 'wholesalerGroups'"
          :checked="mode === 'wholesalerGroups'" />
      </div>

      <label class="search-bar input input-sm flex min-w-96 items-center gap-2">
        <input
          ref="searchInputRef"
          type="text"
          placeholder="Search"
          class="grow"
          v-model="filterText"
          autocomplete="off"
          @keyup="handleSearchKeyUp" />
        <XIcon
          v-if="filterText && filterText.length > 0"
          :size="20"
          class="cursor-pointer"
          @click="filterText = ''" />
        <SearchIcon :size="20" />
      </label>
    </div>

    <div class="inner pt-0">
      <table
        v-if="mode === 'wholesalers'"
        v-tablesort
        id="policy-type-list"
        class="apptable dense mt-2">
        <thead>
          <tr>
            <td
              data-test="wholesalers"
              class="text-left sorted ascending">
              Wholesaler
            </td>
            <td
              data-test="country"
              class="country">
              Country
            </td>
            <td>Wholesaler Group</td>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="wholesaler in sortedWholesalers"
            :key="wholesaler.id"
            data-test="row"
            class="group">
            <td :data-test="`${wholesaler.id}-name`">
              <div class="flex-td">
                {{ wholesaler.name }}
              </div>
            </td>
            <td :data-test="`${wholesaler.id}-country`">
              <div class="flex-td">
                <span>{{ wholesaler.country }}</span>
              </div>
            </td>
            <td :data-test="`${wholesaler.id}-wholesalerGroup`">
              <div class="flex items-center justify-between">
                <span>{{ wholesaler.wholesalerGroup }}</span>
                <button
                  type="button"
                  class="inline-flex cursor-pointer p-1 opacity-30 transition-opacity group-hover:opacity-100"
                  :data-test="`edit-wholesaler-${wholesaler.id}`"
                  title="Edit wholesaler"
                  @click="editingWholesaler = wholesaler">
                  <Edit2 :size="16" />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <table
        v-else-if="mode === 'wholesalerGroups'"
        v-tablesort
        id="policy-type-list"
        class="apptable dense wholesaler-group mt-2">
        <thead>
          <tr>
            <td>Wholesaler Group</td>
            <td>Type</td>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="group in filteredWholesalerGroups"
            :key="group.id"
            data-test="row"
            class="group">
            <td>
              {{ group.name }}
            </td>
            <td class="flex items-center justify-between">
              <div>{{ group.type }}</div>
              <button
                type="button"
                class="inline-flex cursor-pointer p-1 opacity-30 transition-opacity group-hover:opacity-100"
                :data-test="`edit-wholesaler-group-${group.id}`"
                title="Edit wholesaler group"
                @click="editingWholesalerGroup = group">
                <Edit2 :size="16" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <WholesalerUpdateModal
      v-if="editingWholesaler"
      :wholesaler="editingWholesaler"
      :show="true"
      :on-save="submitWholesalerChange"
      :countries="countries"
      :wholesaler-groups="wholesalerGroups"
      @closed="editingWholesaler = null" />
    <WholesalerGroupUpdateModal
      v-if="editingWholesalerGroup"
      :wholesaler-group="editingWholesalerGroup"
      :wholesaler-groups="wholesalerGroups"
      :wholesalers="wholesalers"
      :show="true"
      @change="submitWholesalerGroupUpdate"
      @closed="editingWholesalerGroup = null" />
  </div>
</template>

<script lang="ts">
import { wholesalerService } from '@/services/api';
import WholesalerGroupUpdateModal from '@/components/WholesalerGroupUpdateModal.vue';
import WholesalerUpdateModal from '@/components/WholesalerUpdateModal.vue';
import { WholesalerAdmin } from '@/types/WholesalerAdmin';
import { WholesalerGroup } from '@/types/WholesalerGroup';
import { Edit2, SearchIcon, XIcon } from '@lucide/vue';

//country and group change
export default {
  name: 'Wholesaler',
  components: {
    WholesalerGroupUpdateModal,
    WholesalerUpdateModal,
    SearchIcon,
    XIcon,
    Edit2,
  },
  data: () => ({
    loadingData: true,
    error: undefined,
    filterText: '',
    wholesalers: [] as WholesalerAdmin[],
    wholesalerGroups: [] as WholesalerGroup[],
    countries: [] as string[],
    mode: 'wholesalers',
    editingWholesaler: null as WholesalerAdmin | null,
    editingWholesalerGroup: null as WholesalerGroup | null,
  }),
  computed: {
    sortedWholesalers(): WholesalerAdmin[] {
      let result = [...this.wholesalers];

      if (this.filterText) {
        result = result.filter((item) => {
          const searchText = this.filterText.toLowerCase();
          return (
            item.name.toLowerCase().includes(searchText) ||
            item.country?.toLowerCase().includes(searchText) ||
            item.wholesalerGroup?.toLowerCase().includes(searchText)
          );
        });
      }

      return result.sort((a, b) => {
        const aUndefined = this.isUndefinedWholesalerGroup(a.wholesalerGroup);
        const bUndefined = this.isUndefinedWholesalerGroup(b.wholesalerGroup);
        if (aUndefined && !bUndefined) return -1;
        if (!aUndefined && bUndefined) return 1;
        return 0;
      });
    },
    filteredWholesalerGroups(): WholesalerGroup[] {
      if (!this.filterText) {
        return this.wholesalerGroups;
      }

      const searchText = this.filterText.toLowerCase();
      return this.wholesalerGroups.filter((item) => item.name.toLowerCase().includes(searchText) || item.type?.toLowerCase().includes(searchText));
    },
  },
  mounted() {
    if (this.$refs.searchInputRef) {
      (this.$refs.searchInputRef as HTMLInputElement).focus();
    }
  },
  methods: {
    handleSearchKeyUp(e: KeyboardEvent) {
      if (e.key && e.key === 'Escape') {
        this.filterText = '';
        e.stopPropagation();
      }
    },
    isUndefinedWholesalerGroup(wholesalerGroup: string): boolean {
      return wholesalerGroup === 'Undefined';
    },
    async refreshData() {
      const ws = wholesalerService;
      const [wholesalers, wholesalerGroups] = await Promise.all([ws.list(), ws.wholesalerGroups()]);

      this.wholesalers = (wholesalers || []).sort((a, b) => a.name.localeCompare(b.name));
      this.wholesalerGroups = wholesalerGroups.sort((a, b) => a.name.localeCompare(b.name)) || [];
      this.countries = Array.from(new Set(this.wholesalers.map((x) => x.country)))
        .filter((x) => x != null)
        .sort((a, b) => (a === 'USA' ? -1 : a.localeCompare(b)));

      this.loadingData = false;
    },
    async submitWholesalerChange(id: number, countryName: string, wholesalerGroup: string) {
      const wholesaler = this.wholesalers.find((w) => w.id === id);

      if (!wholesaler) {
        return;
      }

      let wholesalerUpdate = false;

      if (wholesaler.country !== countryName.trim()) {
        wholesalerUpdate = true;
        await wholesalerService.updateCountry({ wholesalerId: id, countryName: countryName.trim() });
      }

      if (wholesaler.wholesalerGroup !== wholesalerGroup.trim()) {
        wholesalerUpdate = true;
        await wholesalerService.updateGroup({ wholesalerId: id, groupName: wholesalerGroup });
      }

      if (wholesalerUpdate) {
        await this.refreshData();
      }
    },
    async submitWholesalerGroupUpdate(wholesalerGroup: WholesalerGroup, deleteFlagged: boolean) {
      if (deleteFlagged) {
        await wholesalerService.deleteWholesalerGroup(wholesalerGroup.id);
      } else {
        await wholesalerService.updateWholesalerGroup(wholesalerGroup.id, wholesalerGroup.name, wholesalerGroup.type);
      }
      return this.refreshData();
    },
  },
  created() {
    this.refreshData();
  },
};
</script>
<style scoped>
.flex-td {
  justify-content: space-between;
  display: inline-flex;
  width: 100%;
}

table.wholesaler-group thead > tr > td {
  width: 50%;
}
</style>
